import { useState } from 'react';
import { Bell, Calendar, Clock, Heart, MessageCircle, Sparkles } from 'lucide-react';
import { Button, EmptyState, PageHeader } from '@/shared/ui';
import { mockNotifications, type MockNotification } from '@/shared/api/mocks/data';
import { formatRelative } from '@/shared/lib/format';
import { cn } from '@/shared/lib/cn';

const ICON_MAP = {
  NEW_MATCH: Sparkles,
  NEW_MESSAGE: MessageCircle,
  MUTUAL_MATCH: Heart,
  CRM_REMINDER: Clock,
  EVENT_REMINDER: Calendar,
} as const;

const COLOR_MAP = {
  NEW_MATCH: 'bg-[#EFF8FF] text-[#175CD3]',
  NEW_MESSAGE: 'bg-[#F4F3FF] text-[#5925DC]',
  MUTUAL_MATCH: 'bg-[#FFF1F3] text-[#C01048]',
  CRM_REMINDER: 'bg-[#FFFAEB] text-[#B54708]',
  EVENT_REMINDER: 'bg-[#ECFDF3] text-[#067647]',
} as const;

export const NotificationsPage = () => {
  const [items, setItems] = useState<MockNotification[]>(mockNotifications);
  const unread = items.filter((n) => !n.read).length;

  const markAll = () => setItems((arr) => arr.map((n) => ({ ...n, read: true })));
  const markOne = (id: string) =>
    setItems((arr) => arr.map((n) => (n.id === id ? { ...n, read: true } : n)));

  return (
    <section className="mx-auto flex w-full max-w-2xl flex-col gap-4">
      <PageHeader
        title="Notificações"
        subtitle={unread > 0 ? `Você tem ${unread} não lidas` : 'Tudo em dia'}
        actions={
          unread > 0 ? (
            <Button variant="outline" size="sm" onClick={markAll}>
              Marcar todas como lidas
            </Button>
          ) : null
        }
      />

      {items.length === 0 ? (
        <EmptyState
          icon={Bell}
          title="Sem notificações"
          description="Tudo limpo por aqui. Aproveite o evento!"
        />
      ) : (
        <ul className="overflow-hidden rounded-xl border border-border bg-background">
          {items.map((n, idx) => {
            const Icon = ICON_MAP[n.tipo];
            const color = COLOR_MAP[n.tipo];
            return (
              <li key={n.id}>
                <button
                  type="button"
                  onClick={() => markOne(n.id)}
                  className={cn(
                    'flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-muted',
                    !n.read && 'bg-[#FAFAFA]',
                  )}
                >
                  <div
                    className={cn(
                      'flex h-9 w-9 shrink-0 items-center justify-center rounded-full',
                      color,
                    )}
                  >
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-3">
                      <p className="truncate text-sm font-semibold text-foreground">{n.titulo}</p>
                      <span className="shrink-0 text-[11px] text-muted-foreground">
                        {formatRelative(n.createdAt)}
                      </span>
                    </div>
                    <p className="mt-0.5 text-xs text-muted-foreground">{n.descricao}</p>
                  </div>
                  {!n.read ? (
                    <span
                      className="ml-1 mt-1 inline-flex h-2 w-2 shrink-0 rounded-full bg-primary"
                      aria-label="Não lida"
                    />
                  ) : null}
                </button>
                {idx < items.length - 1 ? <div className="ml-16 h-px bg-border" /> : null}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};
